import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";
import {
  fetchChannelAnalytics,
  fetchVariantPerformance,
  type ChannelAnalyticsRow,
  type VariantPerformanceRow,
} from "@/lib/shareCampaigns";

const money = (cents: number) => `$${(cents / 100).toFixed(0)}`;
const rate = (num: number, den: number) => (den > 0 ? `${((num / den) * 100).toFixed(1)}%` : "—");

/** Which channels (UTM) and which social-preview variants actually convert. */
export function ChannelAnalyticsPanel() {
  const [channels, setChannels] = useState<ChannelAnalyticsRow[] | null>(null);
  const [variants, setVariants] = useState<VariantPerformanceRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, v] = await Promise.all([fetchChannelAnalytics(), fetchVariantPerformance()]);
      setChannels(c);
      setVariants(v);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const best = (variants ?? [])
    .filter((v) => v.views >= 5)
    .sort((a, b) => b.purchases / Math.max(1, b.views) - a.purchases / Math.max(1, a.views))[0];

  return (
    <section className="rounded-xl border border-border bg-card p-5" aria-labelledby="channel-analytics">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2
            id="channel-analytics"
            className="font-mono text-xs uppercase tracking-wider text-muted-foreground"
          >
            channels &amp; preview variants
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Tag your share links with a campaign, then compare which source and which social
            preview turns clicks into purchases.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
          Refresh
        </Button>
      </div>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <h3 className="font-mono text-[10px] uppercase text-muted-foreground mb-2">by channel</h3>
      {channels && channels.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No tagged traffic yet — add UTM tags from the share dialog's Campaign tab.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono text-[10px] uppercase text-muted-foreground">
                <th className="py-1.5 pr-3">source / medium</th>
                <th className="py-1.5 pr-3">campaign</th>
                <th className="py-1.5 pr-3">views</th>
                <th className="py-1.5 pr-3">signups</th>
                <th className="py-1.5 pr-3">purchases</th>
                <th className="py-1.5 pr-3">conv.</th>
                <th className="py-1.5">revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(channels ?? []).map((r, i) => (
                <tr key={`${r.slug}-${r.utm_source}-${r.utm_medium}-${r.utm_campaign}-${i}`}>
                  <td className="py-2 pr-3">
                    {r.utm_source || "direct"}
                    <span className="text-muted-foreground"> / {r.utm_medium || "—"}</span>
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground">{r.utm_campaign || "—"}</td>
                  <td className="py-2 pr-3">{r.views}</td>
                  <td className="py-2 pr-3">{r.signups}</td>
                  <td className="py-2 pr-3 font-semibold">{r.purchases}</td>
                  <td className="py-2 pr-3">{rate(r.purchases, r.views)}</td>
                  <td className="py-2">{money(r.revenue_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="font-mono text-[10px] uppercase text-muted-foreground mt-6 mb-2">
        preview A/B variants
      </h3>
      {best && (
        <p className="text-sm text-muted-foreground mb-2">
          Best converting variant so far: <span className="font-semibold text-foreground">{best.variant_key}</span>{" "}
          on <span className="font-mono text-[11px]">{best.slug}</span> ({rate(best.purchases, best.views)}).
        </p>
      )}
      {variants && variants.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No variants yet — create title/description/image variants in the share dialog to start a test.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono text-[10px] uppercase text-muted-foreground">
                <th className="py-1.5 pr-3">variant</th>
                <th className="py-1.5 pr-3">link</th>
                <th className="py-1.5 pr-3">views</th>
                <th className="py-1.5 pr-3">signups</th>
                <th className="py-1.5 pr-3">purchases</th>
                <th className="py-1.5 pr-3">conv.</th>
                <th className="py-1.5">revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(variants ?? []).map((v) => (
                <tr key={`${v.share_id}-${v.variant_key}`}>
                  <td className="py-2 pr-3 font-semibold">
                    {v.variant_key}
                    {!v.is_active && (
                      <span className="ml-2 font-mono text-[10px] text-muted-foreground">paused</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 font-mono text-[11px] text-muted-foreground">{v.slug}</td>
                  <td className="py-2 pr-3">{v.views}</td>
                  <td className="py-2 pr-3">{v.signups}</td>
                  <td className="py-2 pr-3 font-semibold">{v.purchases}</td>
                  <td className="py-2 pr-3">{rate(v.purchases, v.views)}</td>
                  <td className="py-2">{money(v.revenue_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
